/**
 * Semana no computador (docs/design/pranchas/00-agenda-aprovada.png): grade de 7 dias de 78 px por hora,
 * hoje com cabeçalho berinjela, linha coral do agora, livres de hoje tracejados dizendo o que cabe e
 * o pedido com Aceitar e Recusar. À direita, o painel Agora, os lembretes de amanhã e Quem sumiu.
 */
import {
  MONTH_NAMES,
  WEEKDAY_SHORT,
  addDays,
  buildAgendaRows,
  daySummary,
  formatHourOf,
  formatMoneyShort,
  freeTimeLabel,
  mergeFreeRows,
  missingMessage,
  nowAndNext,
  parseDate,
  servicesThatFit,
  toIso,
  nextSessionLabel,
  toMs,
  utcToZoned,
  weekdayOf,
  zonedToUtc,
} from '@lindeza/core';
import { type AgendaItem, type WeekDay, useDayAgenda, useMissingClients, useRuntime, useServices, useTomorrow, useWeekAgenda } from '@lindeza/core/hooks';
import { type CSSProperties, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast, toastError, withUndo } from '../../app/ui';
import { openWhatsapp, useMessageCtx } from '../../app/useMessages';
import { IconBell, IconChevronLeft, IconChevronRight, IconCircleCheck, IconLink, IconLock, IconMegaphone, IconPlus, IconTriangleAlert, IconWhatsapp } from '../../components/Icons';
import { Segmented } from '../../components/ui';
import type { SheetState } from './AgendaSheets';
import { AgoraPanel } from './AgoraPanel';
import './week.css';

const PPM = 1.3; // px por minuto (78 px por hora)
const TOP0 = 10;

type Props = {
  date: string;
  today: string;
  now: number;
  timezone: string;
  onPickDate: (d: string) => void;
  onDayView: () => void;
  setSheet: (s: SheetState) => void;
};

const minutesOf = (utc: string | number, tz: string) => utcToZoned(utc, tz).minutes;
const hm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const cap = (s: string) => s.replace(/^./, (c) => c.toUpperCase());

export function WeekView({ date, today, now, timezone: tz, onPickDate, onDayView, setSheet }: Props) {
  const weekStart = addDays(date, -weekdayOf(date));
  const days = useWeekAgenda(weekStart);
  const services = useServices();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Faixa de horas da semana: do primeiro horário aberto ao último (9h às 18h se não houver nada).
  const range = useMemo(() => {
    let lo = Infinity;
    let hi = -Infinity;
    for (const d of days ?? []) {
      for (const r of d.openRanges) {
        lo = Math.min(lo, Number(r.start.slice(0, 2)) * 60 + Number(r.start.slice(3, 5)));
        hi = Math.max(hi, Number(r.end.slice(0, 2)) * 60 + Number(r.end.slice(3, 5)));
      }
      for (const i of d.items) {
        const s = utcToZoned(i.appointment.start_utc, tz);
        if (s.date !== d.date) continue;
        lo = Math.min(lo, s.minutes);
        hi = Math.max(hi, s.minutes + (toMs(i.appointment.end_utc) - toMs(i.appointment.start_utc)) / 60_000);
      }
    }
    if (!Number.isFinite(lo)) return { from: 9 * 60, to: 18 * 60 };
    return { from: Math.floor(lo / 60) * 60, to: Math.min(24 * 60, Math.ceil(hi / 60) * 60) };
  }, [days, tz]);

  const nowMin = minutesOf(now, tz);
  const weekHasToday = today >= weekStart && today <= addDays(weekStart, 6);

  useEffect(() => {
    // Abre com a hora de agora à vista.
    const el = scrollRef.current;
    if (!el || !weekHasToday) return;
    const y = TOP0 + (nowMin - range.from) * PPM;
    if (y > el.clientHeight - 80) el.scrollTop = y - el.clientHeight / 2;
  }, [weekHasToday, nowMin, range.from]);

  if (!days) return <div className="wk-screen" />;

  const cols = [
    'var(--gutw)',
    ...days.map((d) => (d.openRanges.length === 0 && d.items.length === 0 ? '50px' : d.date === today ? 'minmax(0, 1.15fr)' : 'minmax(0, 1fr)')),
  ].join(' ');
  const span = range.to - range.from;
  const last = addDays(weekStart, 6);
  const a = parseDate(weekStart);
  const b = parseDate(last);
  const title = a.month === b.month ? `${cap(MONTH_NAMES[a.month - 1]!)}, semana de ${a.day} a ${b.day}` : `Semana de ${a.day} de ${MONTH_NAMES[a.month - 1]} a ${b.day} de ${MONTH_NAMES[b.month - 1]}`;
  const todayDay = days.find((d) => d.date === today);
  const sum = todayDay ? daySummary(todayDay.items) : null;
  const pendingAll = days.flatMap((d) => d.items.filter((i) => i.appointment.status === 'pending'));
  const firstPendingId = pendingAll.find((i) => toMs(i.appointment.start_utc) > now)?.appointment.id;

  return (
    <div className="wk-screen">
      <main className="wk-main">
        <header className="wk-top">
          <h1 className="wk-title">{title}</h1>
          <div className="wk-nav">
            <button className="btn btn--outline btn--sm" onClick={() => onPickDate(today)}>
              Hoje
            </button>
            <button className="icon-btn icon-btn--outline" aria-label="Semana anterior" onClick={() => onPickDate(addDays(date, -7))}>
              <IconChevronLeft />
            </button>
            <button className="icon-btn icon-btn--outline" aria-label="Próxima semana" onClick={() => onPickDate(addDays(date, 7))}>
              <IconChevronRight />
            </button>
          </div>
          <Segmented
            label="Ver por"
            value="week"
            onChange={(v) => v === 'day' && onDayView()}
            options={[
              { value: 'day', label: 'Dia' },
              { value: 'week', label: 'Semana' },
            ]}
          />
        </header>
        {sum && (
          <p className="wk-sum">
            <b>Hoje:</b>
            <span>
              <b>{sum.count}</b> {sum.count === 1 ? 'atendimento' : 'atendimentos'}
            </span>
            <span>
              <b>{formatMoneyShort(sum.expectedCents)}</b> previstos
            </span>
            <span>
              <b>{formatMoneyShort(todayDay!.receivedCents)}</b> recebidos
            </span>
          </p>
        )}

        <div className="wk" style={{ ['--cols' as string]: cols, ['--span' as string]: span } as CSSProperties}>
          <div className="wk-head">
            <div />
            {days.map((d) => (
              <DayHead key={d.date} day={d} today={today} onPick={() => onPickDate(d.date)} />
            ))}
          </div>
          <div className="wk-scroll" ref={scrollRef}>
            <div className="wk-body">
              <div className="wk-gut">
                {Array.from({ length: span / 60 + 1 }, (_, i) => (
                  <span key={i} style={{ ['--m' as string]: i * 60 } as CSSProperties}>
                    {(range.from + i * 60) / 60}h
                  </span>
                ))}
                {weekHasToday && nowMin >= range.from && nowMin <= range.to && (
                  <b className="now-tag" style={{ ['--m' as string]: nowMin - range.from } as CSSProperties}>
                    {formatHourOf(now, tz)}
                  </b>
                )}
              </div>
              {weekHasToday && nowMin >= range.from && nowMin <= range.to && <i className="wk-now-all" style={{ ['--m' as string]: nowMin - range.from } as CSSProperties} />}
              {days.map((d) => (
                <DayColumn
                  key={d.date}
                  day={d}
                  today={today}
                  now={now}
                  nowMin={nowMin}
                  from={range.from}
                  span={span}
                  timezone={tz}
                  fits={services ?? []}
                  popId={firstPendingId}
                  setSheet={setSheet}
                />
              ))}
            </div>
          </div>
        </div>
      </main>
      <SideColumn today={today} now={now} timezone={tz} setSheet={setSheet} />
    </div>
  );
}

function DayHead({ day, today, onPick }: { day: WeekDay; today: string; onPick: () => void }) {
  const closed = day.openRanges.length === 0 && day.items.length === 0;
  const { count, expectedCents, requests } = daySummary(day.items);
  let value = '';
  if (day.date < today) value = day.receivedCents > 0 ? `${formatMoneyShort(day.receivedCents)} recebidos` : count > 0 ? `${count} ${count === 1 ? 'atendimento' : 'atendimentos'}` : '';
  else if (day.date === today) value = `${formatMoneyShort(expectedCents)} previstos`;
  else if (count === 0 && requests === 0) value = closed ? '' : 'Livre';
  else {
    const parts = [count > 0 ? formatMoneyShort(expectedCents) : '', requests > 0 ? `${requests} ${requests === 1 ? 'pedido' : 'pedidos'}` : ''].filter(Boolean);
    value = parts.join(', ');
  }
  const d = parseDate(day.date).day;
  return (
    <button className={`wk-h${closed ? ' narrow' : ''}${day.date === today ? ' today' : ''}`} onClick={onPick} aria-label={`Ver o dia ${d}`}>
      <span className="dn">
        {WEEKDAY_SHORT[weekdayOf(day.date)]} <b>{d}</b>
        {day.date === today && <span className="dn-k">hoje</span>}
      </span>
      {value && <span className="dv">{value}</span>}
    </button>
  );
}

function DayColumn({
  day,
  today,
  now,
  nowMin,
  from,
  span,
  timezone: tz,
  fits,
  popId,
  setSheet,
}: {
  day: WeekDay;
  today: string;
  now: number;
  nowMin: number;
  from: number;
  span: number;
  timezone: string;
  fits: Array<{ name: string; duration_min: number; active?: boolean }>;
  popId: string | undefined;
  setSheet: (s: SheetState) => void;
}) {
  const rt = useRuntime();
  const closed = day.openRanges.length === 0 && day.items.length === 0;
  const isToday = day.date === today;
  const past = day.date < today;
  const pos = (startUtc: string, endUtc: string): CSSProperties => {
    const s = Math.max(0, minutesOf(startUtc, tz) - from);
    const d = Math.max(20, Math.min(span - s, (toMs(endUtc) - toMs(startUtc)) / 60_000));
    return { ['--s' as string]: s, ['--d' as string]: d } as CSSProperties;
  };
  const gaps = useMemo(
    () =>
      isToday
        ? mergeFreeRows(buildAgendaRows({ date: day.date, timezone: tz, items: day.items, blocks: day.blocks, openRanges: day.openRanges, now, stepMin: 10 })).filter(
            (r): r is Extract<ReturnType<typeof mergeFreeRows<AgendaItem>>[number], { kind: 'free' }> => r.kind === 'free',
          )
        : [],
    [isToday, day, tz, now],
  );

  function onColumnClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target !== e.currentTarget || closed || past) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const m = Math.round(((e.clientY - rect.top - TOP0) / PPM + from) / 30) * 30;
    const start = zonedToUtc(day.date, hm(Math.max(0, Math.min(24 * 60 - 30, m))), tz);
    if (start < now - 30 * 60_000) return;
    setSheet({ kind: 'new', start: toIso(start) });
  }

  return (
    <div
      className={`dcol${closed ? ' closed' : ' open'}${isToday ? ' today' : ''}${past ? ' past' : ''}`}
      onClick={onColumnClick}
      role="presentation"
    >
      {closed && <p className="folga">Folga</p>}
      {!closed && !past && day.items.length === 0 && day.blocks.length === 0 && (
        <div className="wk-empty">
          <p>
            Nenhuma cliente ainda.<span className="more"> Mande seu link no WhatsApp e no Instagram.</span>
          </p>
          <Link className="btn btn--soft" to="/divulgar">
            <IconMegaphone width={18} height={18} /> Divulgar
          </Link>
        </div>
      )}
      {day.blocks.map((b) => (
        <button key={b.id} className="wk-blk wk-lock" style={pos(b.start_utc, b.end_utc)} onClick={() => setSheet({ kind: 'block', start: b.start_utc, blockId: b.id })}>
          <IconLock /> {b.reason ?? 'Bloqueado'}
        </button>
      ))}
      {gaps.map((g) =>
        g.minutes < 30 ? (
          <div key={g.start} className="wk-free tiny" style={pos(g.start, g.end)}>
            {freeTimeLabel(g.minutes)}
          </div>
        ) : (
          <button key={g.start} className="wk-free" style={pos(g.start, g.end)} onClick={() => setSheet({ kind: 'new', start: g.start })} data-testid={`week-free-${g.time}`}>
            <span className="fr-t">{freeTimeLabel(g.minutes).replace(' horas ', 'h ').replace(' hora ', 'h ')}</span>
            {g.minutes >= 60 && servicesThatFit(fits, g.minutes).length > 0 && (
              <span className="fr-fits">
                Cabe{' '}
                {servicesThatFit(fits, g.minutes)
                  .map((s) => s.name.toLowerCase())
                  .join(' ou ')}
              </span>
            )}
            {g.minutes * PPM >= 120 && (
              <span className="btn btn--soft fr-btn" aria-hidden="true">
                <IconPlus /> Marcar
              </span>
            )}
          </button>
        ),
      )}
      {day.items.map((item) => {
        const a = item.appointment;
        const style = pos(a.start_utc, a.end_utc);
        if (a.status === 'pending') {
          const s = Number((style as Record<string, number>)['--s']);
          const up = s > span - 150;
          return (
            <div key={a.id}>
              <button className="wk-req" style={style} onClick={() => setSheet({ kind: 'appt', id: a.id })}>
                <span className="rq-name">{item.client?.name ?? 'Cliente'}</span>
                <span className="rq-k">Pedido, {item.service?.name.toLowerCase()}</span>
              </button>
              {a.id === popId && (
                <div
                  className={`wk-pop${up ? ' up' : ''}`}
                  style={{ ['--s' as string]: up ? s : s + Number((style as Record<string, number>)['--d']) } as CSSProperties}
                  role="group"
                  aria-label={`Pedido de ${item.client?.name ?? 'cliente'}`}
                >
                  <p className="when">
                    {cap(dayName(day.date))}, {formatHourOf(a.start_utc, tz)} às {formatHourOf(a.end_utc, tz)}
                  </p>
                  <div className="btns">
                    <button className="btn btn--primary" onClick={() => void rt.actions.accept(a.id).then(() => toast('Horário aceito.')).catch(toastError)}>
                      Aceitar
                    </button>
                    <button
                      className="btn btn--outline"
                      onClick={() =>
                        void rt.actions
                          .cancel(a.id)
                          .then(() => withUndo('Pedido recusado.', () => rt.actions.setStatus(a.id, 'pending').then(() => undefined)))
                          .catch(toastError)
                      }
                    >
                      Recusar
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        }
        const isNow = isToday && toMs(a.start_utc) <= now && now < toMs(a.end_utc);
        const tall = (toMs(a.end_utc) - toMs(a.start_utc)) / 60_000 >= 60;
        return (
          <button
            key={a.id}
            className={`wk-blk${isNow ? ' is-now' : ''}${item.conflict ? ' is-conflict' : ''}`}
            style={{ ...style, ['--c' as string]: item.service?.color ?? undefined } as CSSProperties}
            onClick={() => setSheet({ kind: 'appt', id: a.id })}
            data-testid="week-appointment"
          >
            <span className="b-name">
              <span className="dot" />
              <span>{item.client?.name ?? 'Cliente'}</span>
            </span>
            {tall && (
              <span className="b-sub">
                {formatHourOf(a.start_utc, tz)} às {formatHourOf(a.end_utc, tz)}
                {a.status === 'completed' && <IconCircleCheck className="done" />}
                {a.source === 'link' && a.status !== 'completed' && <IconLink className="done" style={{ color: 'var(--text-muted)' }} />}
              </span>
            )}
            <span className="b-sub">
              {item.service?.name}
              {item.packageBalance ? `, ${nextSessionLabel(item.packageBalance)}` : ''}
            </span>
            {tall && !past && a.status === 'scheduled' && a.source !== 'link' && (
              <span className="b-sub b-warn">
                <IconTriangleAlert /> Não confirmou
              </span>
            )}
            {tall && !past && a.status === 'confirmed' && <span className="b-sub b-ok">Confirmada</span>}
          </button>
        );
      })}
      {isToday && nowMin >= from && nowMin <= from + span && <i className="wk-now-today" style={{ ['--m' as string]: nowMin - from } as CSSProperties} />}
    </div>
  );
}

function dayName(date: string): string {
  return ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'][weekdayOf(date)]!;
}

/** Coluna da direita: painel Agora, lembretes de amanhã e Quem sumiu. */
function SideColumn({ today, now, timezone: tz, setSheet }: { today: string; now: number; timezone: string; setSheet: (s: SheetState) => void }) {
  const day = useDayAgenda(today);
  const tomorrow = useTomorrow();
  const missing = useMissingClients();
  const ctx = useMessageCtx();
  const { current, next } = useMemo(() => (day ? nowAndNext(day.items, now) : { current: null, next: null }), [day, now]);
  const panelItem = current ?? next;
  const sent = remindersSent(today);
  return (
    <aside className="wk-side" aria-label="Hoje">
      {panelItem && (
        <AgoraPanel
          item={panelItem}
          after={current ? next : null}
          now={now}
          timezone={tz}
          onCharge={() => setSheet({ kind: 'pay', id: panelItem.appointment.id })}
          onOpen={() => setSheet({ kind: 'appt', id: panelItem.appointment.id })}
        />
      )}
      {tomorrow && tomorrow.length > 0 && (
        <section className="wk-tm" aria-label="Amanhã">
          <div className="wk-tm-h">
            <IconBell />
            <div>
              <b>
                Amanhã: {tomorrow.length} {tomorrow.length === 1 ? 'cliente' : 'clientes'}
              </b>
              <span className={sent ? 'ok' : undefined}>{sent ? 'Lembretes enviados' : 'Lembretes não enviados'}</span>
            </div>
          </div>
          <p className="wk-tm-list">
            {tomorrow
              .slice(0, 4)
              .map((t) => `${t.client?.name.split(' ')[0] ?? 'Cliente'} ${formatHourOf(t.appointment.start_utc, tz)}`)
              .join(', ')}
            {tomorrow.length > 4 ? ` e mais ${tomorrow.length - 4}` : ''}
          </p>
          <button className="btn btn--whatsapp btn--block" onClick={() => setSheet({ kind: 'tomorrow' })}>
            <IconWhatsapp /> Mandar lembretes
          </button>
        </section>
      )}
      {missing && missing.length > 0 && ctx && (
        <section aria-label="Quem sumiu">
          <h2 className="wk-gone-t">
            Quem sumiu <span>{missing.length} {missing.length === 1 ? 'cliente' : 'clientes'}</span>
          </h2>
          {missing.slice(0, 3).map((m) => (
            <div key={m.client.id} className="wk-gone-row">
              <div>
                <Link to={`/clientes/${m.client.id}`}>{m.client.name}</Link>
                {m.lastService && <span>{m.lastService.name}</span>}
                <span>{m.daysSince} dias sem vir</span>
              </div>
              <button
                className="btn btn--whatsapp btn--sm"
                aria-label={`Chamar ${m.client.name}`}
                onClick={() => openWhatsapp(m.client.whatsapp, missingMessage(ctx, m.client, m.lastService, m.weeksSince))}
              >
                <IconWhatsapp /> Chamar
              </button>
            </div>
          ))}
          {missing.length > 3 && (
            <Link className="btn btn--link btn--sm" to="/clientes">
              Ver todas
            </Link>
          )}
        </section>
      )}
    </aside>
  );
}

function remindersSent(today: string): boolean {
  try {
    return localStorage.getItem(`lindeza:reminders:${today}`) === '1';
  } catch {
    return false;
  }
}

